/**
 * Selector hook: returns the live ``SessionConversation`` for one
 * Projects ``tool_call_id`` driving an ``answer_with_evidence`` /
 * ``lookup_evidence`` call.
 *
 * The state itself is owned by ``evidenceSessionsAtom`` and updated by
 * the agent-event bus handler (see
 * ``src/features/projects/state/evidence-sessions.ts``). This hook is
 * the read-side: the Projects message renderer calls it with the
 * tool_call_id from a tool_call block, and renders the returned
 * timeline using the canonical Evidence leaf blocks.
 *
 * Returns ``undefined`` while the sub-agent has not yet emitted any
 * chunks for this call — that's the cue for the renderer to show a
 * generic "starting" loader instead of an empty Evidence timeline.
 */

import type { SessionConversation } from '@/components/chat/types/types';
import { evidenceSessionsAtom } from '@/features/projects/state/evidence-sessions';
import { useAtomValue } from 'jotai';

export const useProjectEvidenceSession = (
  toolCallId: string | undefined
): SessionConversation | undefined => {
  // Subscribe to the whole map and key-look up on the consumer side.
  // ``selectAtom`` would let us re-render only when *this*
  // ``toolCallId`` changes (rather than on every sub-agent call) but
  // adds a hooks-rule footgun: the memoised slice atom must outlive
  // its consumer, otherwise jotai's atom registry crashes with "Atom
  // is undefined or null". The Map-get path is fine in practice —
  // each Projects bubble holds one inline block at most, so the
  // re-render fan-out is bounded by message count, not by
  // sub-agent-chunk fan-out.
  const sessions = useAtomValue(evidenceSessionsAtom);
  return toolCallId ? sessions.get(toolCallId) : undefined;
};
